import { Component, inject, ChangeDetectionStrategy, computed } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountService } from '../../../core/services/account.service';
import { AuthService } from '../../../core/auth/auth.service';
import { UserRole, ROLE_LEVELS } from '../../../core/auth/role.model';
import { AccountEditDialogComponent } from '../account-edit-dialog/account-edit-dialog.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog';
import { firstValueFrom } from 'rxjs';
import type { Account } from '../../../shared/interfaces/account.interface';

const ROLE_MAP: Record<UserRole, string> = {
  'super_admin': 'スーパー管理者',
  'org_admin': 'テナント管理者',
  'dep_admin': '部門管理者',
  'site_manager': '現場管理者',
  'viewer': '閲覧者'
};

@Component({
  selector: 'app-account-list',
  imports: [
    RouterModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold">ユーザー管理</h1>
        <a matButton="tonal" routerLink="/accounts/create">
          <mat-icon>add</mat-icon>
          ユーザー追加
        </a>
      </div>

      @if (accountService.isLoading()) {
        <div class="flex justify-center items-center py-12">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else if (accountService.error()) {
        <div class="bg-red-50 text-red-600 p-4 rounded-lg">
          {{ accountService.error() }}
        </div>
      } @else if (accountService.accounts().length === 0) {
        <div class="bg-primary-container p-12 text-center rounded-lg shadow-sm">
          <mat-icon class="text-4xl text-gray-400 mb-2">group_off</mat-icon>
          <p class="text-gray-500">アカウントが登録されていません</p>
        </div>
      } @else {
        <div class="bg-primary-container rounded-lg shadow-sm overflow-hidden">
          <table mat-table [dataSource]="sortedAccounts()" class="w-full">
            
            <ng-container matColumnDef="displayName">
              <th mat-header-cell *matHeaderCellDef> 名前 </th>
              <td mat-cell *matCellDef="let account"> {{account.displayName || '未設定'}} </td>
            </ng-container>

            <ng-container matColumnDef="user">
              <th mat-header-cell *matHeaderCellDef> メールアドレス </th>
              <td mat-cell *matCellDef="let account"> {{account.user}} </td>
            </ng-container>

            <ng-container matColumnDef="role">
              <th mat-header-cell *matHeaderCellDef> ロール </th>
              <td mat-cell *matCellDef="let account"> 
                <span class="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-600">
                  {{ getRoleName(account.role) }}
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="w-16"></th>
              <td mat-cell *matCellDef="let account">
                @if (canManageAccount()) {
                  <button matIconButton aria-label="アカウント操作" [matMenuTriggerFor]="menu">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #menu="matMenu" xPosition="before">
                    <button mat-menu-item (click)="editAccount(account)">
                      <mat-icon>edit</mat-icon>
                      <span>編集</span>
                    </button>
                    <button mat-menu-item [disabled]="!canDeleteAccount(account)" (click)="deleteAccount(account)">
                      <mat-icon class="text-red-600!">delete</mat-icon>
                      <span class="text-red-600!">削除</span>
                    </button>
                  </mat-menu>
                }
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="hover:bg-gray-50"></tr>
          </table>
        </div>
      }
    </div>
  `
})
export class AccountListComponent {
  protected readonly accountService = inject(AccountService);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  
  public sortedAccounts = computed(() => {
    const accounts = this.accountService.accounts();
    if (!accounts) return [];
    
    return [...accounts].sort((a, b) => {
      const roleA = a.role ? ROLE_LEVELS[a.role as UserRole] ?? 0 : 0;
      const roleB = b.role ? ROLE_LEVELS[b.role as UserRole] ?? 0 : 0;
      
      if (roleA !== roleB) {
        return roleB - roleA; // 降順: super_admin(100) -> org_admin(80) -> ...
      }
      
      const emailA = a.user || '';
      const emailB = b.user || '';
      return emailA.localeCompare(emailB);
    });
  });

  public displayedColumns: string[] = ['displayName', 'user', 'role', 'actions'];

  public getRoleName(role?: UserRole | string): string {
    return role ? (ROLE_MAP[role as UserRole] || role) : '未設定';
  }

  public canManageAccount(): boolean {
    const role = this.authService.userRole();
    return role === 'super_admin' || role === 'org_admin';
  }

  public canDeleteAccount(account: Account): boolean {
    return account.id !== this.authService.user()?.uid;
  }

  public async editAccount(account: Account): Promise<void> {
    const dialogRef = this.dialog.open(AccountEditDialogComponent, {
      width: '480px',
      data: { displayName: account.displayName || '' }
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      try {
        const newDisplayName = result.displayName;
        const password = result.password;
        
        // Update displayName if it has changed (even to empty string)
        const displayNameToUpdate = newDisplayName !== (account.displayName || '') ? newDisplayName : undefined;
        
        if (displayNameToUpdate !== undefined || password) {
          await this.accountService.updateAccount(account.id, displayNameToUpdate, password);
          this.snackBar.open('アカウントを更新しました', '閉じる', { duration: 3000 });
          this.accountService.reload();
        }
      } catch (err) {
        console.error(err);
        this.snackBar.open('アカウントの更新に失敗しました', '閉じる', { duration: 5000, panelClass: 'error-snackbar' });
      }
    }
  }

  public async deleteAccount(account: Account): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'アカウントの削除',
        message: `${account.displayName || account.user} を削除しますか？この操作は取り消せません。`,
        confirmText: '削除',
        color: 'warn'
      }
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (confirmed) {
      try {
        await this.accountService.deleteAccount(account.id);
        this.snackBar.open('アカウントを削除しました', '閉じる', { duration: 3000 });
        this.accountService.reload();
      } catch (err) {
        console.error(err);
        this.snackBar.open('アカウントの削除に失敗しました', '閉じる', { duration: 5000, panelClass: 'error-snackbar' });
      }
    }
  }
}
